import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "./CartContext";
import Navbar from "./Navbar";

// Professional metric statistics
const METRIC_TARGETS = {
  customers: 2147,
  servicesBooked: 2275,
  testimonials: 234,
  providedServices: 2106,
};

// Standard currency formatting for Indian market
const formatINR = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

const cleanNumericPrice = (price) => {
  if (typeof price === "number") return Number.isFinite(price) ? price : 0;
  if (typeof price === "string") {
    const cleaned = price.replace(/[^\d.]/g, "");
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export default function Cart() {
  const { cart = [], removeFromCart, clearCart, updateQuantity } = useCart();
  const navigate = useNavigate();

  // Metric ticker state
  const [counts, setCounts] = useState({
    customers: 0,
    servicesBooked: 0,
    testimonials: 0,
    providedServices: 0,
  });

  // Smooth ease-out animation using requestAnimationFrame
  useEffect(() => {
    let animationFrameId;
    const duration = 1600;
    const startTimestamp = performance.now();

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animate = (currentTimestamp) => {
      const elapsed = currentTimestamp - startTimestamp;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);

      setCounts({
        customers: Math.round(METRIC_TARGETS.customers * easedProgress),
        servicesBooked: Math.round(
          METRIC_TARGETS.servicesBooked * easedProgress,
        ),
        testimonials: Math.round(METRIC_TARGETS.testimonials * easedProgress),
        providedServices: Math.round(
          METRIC_TARGETS.providedServices * easedProgress,
        ),
      });

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  // Handlers
  const handleIncrease = useCallback(
    (item) => {
      const currentQty = Number(item.quantity) || 1;
      updateQuantity(item.id, currentQty + 1);
    },
    [updateQuantity],
  );

  const handleDecrease = useCallback(
    (item) => {
      const currentQty = Number(item.quantity) || 1;
      if (currentQty > 1) {
        updateQuantity(item.id, currentQty - 1);
      } else {
        removeFromCart(item.id);
      }
    },
    [updateQuantity, removeFromCart],
  );

  const handleClear = useCallback(() => {
    clearCart();
    localStorage.removeItem("cartCache");
  }, [clearCart]);

  // Financial calculations
  const { subtotal, estimatedTax, grandTotal } = useMemo(() => {
    const sub = cart.reduce((accum, item) => {
      const price = cleanNumericPrice(item.price);
      const qty = Number(item.quantity) || 1;
      return accum + price * qty;
    }, 0);

    return {
      subtotal: sub,

      grandTotal: sub,
    };
  }, [cart]);

  return (
    <div
      className="min-vh-100"
      style={{
        backgroundColor: "#f8fafc",
        fontFamily:
          "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: "#0f172a",
      }}
    >
      <Navbar />

      <main className="container py-4 py-lg-5" style={{ maxWidth: "1120px" }}>
        {/* Header section */}
        <header className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-2"
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor: "#e0e7ff",
                  color: "#4338ca",
                }}
              >
                <svg
                  width="18"
                  height="18"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M0 2.5A.5.5 0 0 1 .5 2H2a.5.5 0 0 1 .485.379L2.89 4H14.5a.5.5 0 0 1 .485.621l-1.5 6A.5.5 0 0 1 13 11H4a.5.5 0 0 1-.485-.379L1.61 3H.5a.5.5 0 0 1-.5-.5M3.14 5l1.25 5h8.22l1.25-5zM5 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2m-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0m8-1a1 1 0 1 0 0 2 1 1 0 0 0 0-2m-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0" />
                </svg>
              </span>
              <h1 className="h4 fw-bold mb-0 text-slate-900">Your Cart</h1>
              <span
                className="badge rounded-pill px-2.5 py-1 ms-1"
                style={{
                  backgroundColor: "#e2e8f0",
                  color: "#334155",
                  fontSize: "0.75rem",
                }}
              >
                {cart.length} {cart.length === 1 ? "item" : "items"}
              </span>
            </div>
            <p className="text-secondary small mb-0">
              Review selected items and proceed to secure checkout.
            </p>
          </div>

          {cart.length > 0 && (
            <button
              onClick={handleClear}
              className="btn btn-sm btn-outline-danger px-3 rounded-pill fw-medium border-opacity-50"
            >
              Clear Cart
            </button>
          )}
        </header>

        {/* Content Body */}
        {cart.length === 0 ? (
          /* High-polish Empty Cart State */
          <div
            className="text-center py-5 px-3 bg-white rounded-4 border shadow-xs my-3"
            style={{ borderColor: "#e2e8f0" }}
          >
            <div
              className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: "72px",
                height: "72px",
                backgroundColor: "#f1f5f9",
                color: "#94a3b8",
              }}
            >
              <svg
                width="34"
                height="34"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M8 1a2.5 2.5 0 0 0-2.5 2.5V4h5v-.5A2.5 2.5 0 0 0 8 1m3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
              </svg>
            </div>
            <h2 className="h5 fw-bold text-slate-800 mb-1">
              Your cart is completely empty
            </h2>
            <p
              className="text-secondary small mb-4 mx-auto"
              style={{ maxWidth: "340px" }}
            >
              Explore our services catalog to add the items you need to get
              started.
            </p>
            <button
              onClick={() => navigate("/")}
              className="btn btn-primary px-4 py-2 rounded-3 fw-semibold shadow-sm"
              style={{ backgroundColor: "#8dcae4", borderColor: "#8dcae4" }}
            >
              Explore Services
            </button>
          </div>
        ) : (
          /* Split Grid Layout */
          <div className="row g-4 align-items-start">
            {/* Items Column */}
            <div className="col-lg-8">
              <div className="d-flex flex-column gap-3">
                {cart.map((item) => {
                  const unitPrice = cleanNumericPrice(item.price);
                  const quantity = Number(item.quantity) || 1;
                  const itemTotal = unitPrice * quantity;

                  return (
                    <article
                      key={item.id}
                      className="bg-white rounded-3 p-3 border shadow-xs transition-all"
                      style={{ borderColor: "#e2e8f0" }}
                    >
                      <div className="d-flex gap-3 align-items-center">
                        {/* Thumbnail */}
                        <div
                          className="rounded-3 overflow-hidden flex-shrink-0 bg-light d-flex align-items-center justify-content-center border"
                          style={{
                            width: "72px",
                            height: "72px",
                            borderColor: "#f1f5f9",
                          }}
                        >
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-100 h-100 object-fit-cover"
                            />
                          ) : (
                            <svg
                              width="24"
                              height="24"
                              fill="#94a3b8"
                              viewBox="0 0 16 16"
                            >
                              <path d="M6 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5m-2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5m-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5" />
                            </svg>
                          )}
                        </div>

                        {/* Details */}
                        <div className="flex-grow-1 min-w-0">
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <h3 className="h6 fw-semibold text-truncate mb-1 text-slate-900">
                              {item.name}
                            </h3>
                            <span className="fw-bold text-slate-900">
                              {formatINR(itemTotal)}
                            </span>
                          </div>

                          <div className="d-flex align-items-center gap-2 mb-2 text-muted small">
                            <span>{formatINR(unitPrice)} each</span>
                            {item.duration && (
                              <>
                                <span>•</span>
                                <span>{item.duration}</span>
                              </>
                            )}
                          </div>

                          {/* Controls */}
                          <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                            {/* Stepper */}
                            <div
                              className="d-inline-flex align-items-center rounded-2 border"
                              style={{
                                borderColor: "#cbd5e1",
                                backgroundColor: "#fff",
                              }}
                            >
                              <button
                                type="button"
                                className="btn btn-sm px-2 py-0 border-0 text-slate-600"
                                onClick={() => handleDecrease(item)}
                                aria-label="Decrease quantity"
                                style={{ lineHeight: 1.5 }}
                              >
                                −
                              </button>
                              <span
                                className="px-2 small fw-semibold text-center"
                                style={{ minWidth: "28px" }}
                              >
                                {quantity}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm px-2 py-0 border-0 text-slate-600"
                                onClick={() => handleIncrease(item)}
                                aria-label="Increase quantity"
                                style={{ lineHeight: 1.5 }}
                              >
                                +
                              </button>
                            </div>

                            {/* Remove button */}
                            <button
                              type="button"
                              onClick={() => removeFromCart(item.id)}
                              className="btn btn-link link-secondary p-0 text-decoration-none small text-danger-hover"
                              style={{ fontSize: "0.825rem" }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            {/* Order Summary Sticky Column */}
            <div className="col-lg-4 position-sticky" style={{ top: "1.5rem" }}>
              <div
                className="card bg-white p-4 rounded-3 border shadow-xs"
                style={{ borderColor: "#e2e8f0" }}
              >
                <h4
                  className="fw-bold text-uppercase tracking-wide mb-3"
                  style={{
                    fontSize: "0.8rem",
                    color: "#64748b",
                    letterSpacing: "0.05em",
                  }}
                >
                  Order Summary
                </h4>

                <div className="d-flex justify-content-between mb-2 small text-slate-600">
                  <span>Subtotal</span>
                  <span className="fw-medium text-slate-900">
                    {formatINR(subtotal)}
                  </span>
                </div>

                <hr className="my-3" style={{ borderColor: "#f1f5f9" }} />

                <div className="d-flex justify-content-between align-items-baseline mb-4">
                  <span className="fw-bold text-slate-900">Total</span>
                  <span
                    className="h4 fw-bold mb-0"
                    style={{ color: "#7ad4fa" }}
                  >
                    {formatINR(grandTotal)}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/Checkout")}
                  className="btn w-100 py-2.5 rounded-3 fw-semibold text-white shadow-sm d-flex align-items-center justify-content-center gap-2"
                  style={{ backgroundColor: "#8dcae4", border: "none" }}
                >
                  <span>Proceed to Checkout</span>
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8"
                    />
                  </svg>
                </button>

                {/* Trust Badge */}
                <div
                  className="mt-3 p-3 rounded-2 border d-flex gap-2 align-items-center"
                  style={{ backgroundColor: "#f8fafc", borderColor: "#f1f5f9" }}
                >
                  <svg
                    width="18"
                    height="18"
                    fill="#16a34a"
                    viewBox="0 0 16 16"
                    className="flex-shrink-0"
                  >
                    <path d="M8 0c-.69 0-1.843.265-2.928.56-1.11.3-2.229.655-2.887.87a1.54 1.54 0 0 0-1.044 1.262c-.596 4.477.787 7.795 2.464 9.99a11.8 11.8 0 0 0 4.048 3.161.7.7 0 0 0 .694 0 11.8 11.8 0 0 0 4.048-3.161c1.677-2.195 3.06-5.513 2.464-9.99a1.54 1.54 0 0 0-1.044-1.263 63 63 0 0 0-2.887-.87C9.843.266 8.69 0 8 0m2.146 5.146a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793z" />
                  </svg>
                  <span
                    className="small text-muted"
                    style={{ fontSize: "0.78rem" }}
                  >
                    Guaranteed 256-bit SSL encrypted checkout
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Real-time Metric Ticker */}
        <section className="mt-5 pt-4 border-top">
          <div className="row g-3">
            {[
              { label: "Active Customers", value: counts.customers },
              { label: "Booked Services", value: counts.servicesBooked },
              { label: "5-Star Reviews", value: counts.testimonials },
              { label: "Completed Orders", value: counts.providedServices },
            ].map((stat, idx) => (
              <div key={idx} className="col-6 col-md-3">
                <div
                  className="p-3 bg-white rounded-3 border text-center h-100 d-flex flex-column justify-content-center"
                  style={{ borderColor: "#e2e8f0" }}
                >
                  <span className="h4 fw-bold mb-0 text-slate-900">
                    {stat.value.toLocaleString("en-IN")}
                  </span>
                  <span className="text-secondary small mt-1">
                    {stat.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
